/**
 * Meeting Intelligence Panel
 * AI-powered meeting workflow: recording upload, transcription, pre-meeting briefs,
 * post-meeting follow-ups, and upcoming meeting schedule with calendar integration.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Brain,
  Mic,
  Video,
  FileText,
  Calendar,
  Clock,
  Play,
  Loader2,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  ListChecks,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Send,
  Plus,
  Upload,
} from 'lucide-react';
import {
  meetingIntelligenceAPI,
  activitiesAPI,
  Activity,
  MeetingTranscription,
  UpcomingSalesMeeting,
} from '@/services/api/salesPipelineAPI';
import { MeetingRecorder, RecordingResult } from './MeetingRecorder';

interface MeetingIntelligencePanelProps {
  opportunityId: string;
}

export const MeetingIntelligencePanel: React.FC<MeetingIntelligencePanelProps> = ({ opportunityId }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState<UpcomingSalesMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'upcoming' | 'past'>('upcoming');

  // Recording upload
  const [showRecordingForm, setShowRecordingForm] = useState<string | null>(null);
  const [recordingMode, setRecordingMode] = useState<'upload' | 'record' | 'url'>('upload');
  const [audioUrl, setAudioUrl] = useState('');
  const [recordingFile, setRecordingFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Log meeting form
  const [showLogMeetingForm, setShowLogMeetingForm] = useState(false);
  const [logMeetingTitle, setLogMeetingTitle] = useState('');
  const [logMeetingDate, setLogMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const [logMeetingNotes, setLogMeetingNotes] = useState('');
  const [loggingMeeting, setLoggingMeeting] = useState(false);

  // Brief/Follow-up
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [briefResult, setBriefResult] = useState<any>(null);
  const [generatingFollowUp, setGeneratingFollowUp] = useState<string | null>(null);
  const [followUpResult, setFollowUpResult] = useState<{ activityId: string; data: any } | null>(null);

  // Transcription viewer
  const [viewingTranscription, setViewingTranscription] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<MeetingTranscription | null>(null);
  const [loadingTranscription, setLoadingTranscription] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load activities and meetings independently — don't let one failure block the other
      const [actsResult, meetingsResult] = await Promise.allSettled([
        activitiesAPI.getActivities({ opportunityId, type: 'meeting' }),
        meetingIntelligenceAPI.getUpcomingMeetings(14),
      ]);
      if (actsResult.status === 'fulfilled') {
        setActivities(actsResult.value);
      }
      if (meetingsResult.status === 'fulfilled') {
        const meetings = meetingsResult.value;
        setUpcomingMeetings((meetings || []).filter((m: any) => m.opportunityId === opportunityId));
      }
    } catch (err) {
      console.error('Failed to load meeting data:', err);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleProcessRecording = async (activityId: string) => {
    if (!audioUrl) return;
    setProcessing(activityId);
    try {
      await meetingIntelligenceAPI.processRecording(activityId, audioUrl, opportunityId);
      setShowRecordingForm(null);
      setAudioUrl('');
      await loadData();
    } catch (err) {
      console.error('Failed to process recording:', err);
    } finally {
      setProcessing(null);
    }
  };

  const handleFileUpload = async (activityId: string, file: File | Blob) => {
    setProcessing(activityId);
    try {
      await meetingIntelligenceAPI.uploadAndProcessRecording(activityId, file, opportunityId);
      setShowRecordingForm(null);
      setRecordingFile(null);
      await loadData();
    } catch (err) {
      console.error('Failed to upload recording:', err);
    } finally {
      setProcessing(null);
    }
  };

  const handleRecordingComplete = async (activityId: string, result: RecordingResult) => {
    setProcessing(activityId);
    try {
      await meetingIntelligenceAPI.uploadAndProcessRecording(activityId, result.blob, opportunityId);
      setShowRecordingForm(null);
      await loadData();
    } catch (err) {
      console.error('Failed to upload recording:', err);
    } finally {
      setProcessing(null);
    }
  };

  // Create a new meeting activity and optionally upload a recording to it
  const handleStandaloneSubmit = async () => {
    const title = meetingTitle.trim() || `Meeting ${new Date().toLocaleDateString()}`;
    setProcessing('__standalone__');
    try {
      // Step 1: Create the meeting activity
      const newActivity = await activitiesAPI.createActivity({
        type: 'meeting',
        title,
        description: '',
        contact: '',
        opportunityId,
        dueDate: meetingDate,
        completed: true,
        completedAt: new Date().toISOString(),
        priority: 'medium',
      });

      // Step 2: Upload recording or process URL if provided
      if (recordingFile && newActivity?.id) {
        await meetingIntelligenceAPI.uploadAndProcessRecording(newActivity.id, recordingFile, opportunityId);
      } else if (audioUrl && newActivity?.id) {
        await meetingIntelligenceAPI.processRecording(newActivity.id, audioUrl, opportunityId);
      }

      // Reset form
      setShowRecordingForm(null);
      setRecordingFile(null);
      setAudioUrl('');
      setMeetingTitle('');
      setActiveSection('past');
      await loadData();
    } catch (err) {
      console.error('Failed to create meeting:', err);
    } finally {
      setProcessing(null);
    }
  };

  // Log a past meeting without a recording
  const handleLogMeeting = async () => {
    if (!logMeetingTitle.trim()) return;
    setLoggingMeeting(true);
    try {
      await activitiesAPI.createActivity({
        type: 'meeting',
        title: logMeetingTitle.trim(),
        description: logMeetingNotes,
        contact: '',
        opportunityId,
        dueDate: logMeetingDate,
        completed: true,
        completedAt: new Date().toISOString(),
        priority: 'medium',
      });
      setShowLogMeetingForm(false);
      setLogMeetingTitle('');
      setLogMeetingNotes('');
      setActiveSection('past');
      await loadData();
    } catch (err) {
      console.error('Failed to log meeting:', err);
    } finally {
      setLoggingMeeting(false);
    }
  };

  const handleGenerateBrief = async () => {
    setGeneratingBrief(true);
    setBriefResult(null);
    try {
      const result = await meetingIntelligenceAPI.generatePreMeetingBrief(opportunityId);
      setBriefResult(result);
    } catch (err: any) {
      console.error('Failed to generate brief:', err);
      setBriefResult({ success: false, error: err?.response?.data?.detail || 'Failed to generate brief. AI service may not be configured.' });
    } finally {
      setGeneratingBrief(false);
    }
  };

  const handleGenerateFollowUp = async (activityId: string) => {
    setGeneratingFollowUp(activityId);
    setFollowUpResult(null);
    try {
      const result = await meetingIntelligenceAPI.generateFollowUp(activityId);
      setFollowUpResult({ activityId, data: result });
    } catch (err) {
      console.error('Failed to generate follow-up:', err);
    } finally {
      setGeneratingFollowUp(null);
    }
  };

  const handleViewTranscription = async (activityId: string) => {
    if (viewingTranscription === activityId) {
      setViewingTranscription(null);
      return;
    }
    setViewingTranscription(activityId);
    setLoadingTranscription(true);
    try {
      const result = await meetingIntelligenceAPI.getTranscription(activityId);
      setTranscription(result);
    } catch (err) {
      console.error('Failed to load transcription:', err);
      setTranscription(null);
    } finally {
      setLoadingTranscription(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" />
        <span className="text-sm text-gray-500">Loading meeting data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* AI Pre-Meeting Brief */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-purple-600" />
            <h4 className="text-sm font-semibold text-gray-700">AI Pre-Meeting Brief</h4>
          </div>
          <button
            onClick={handleGenerateBrief}
            disabled={generatingBrief}
            className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 disabled:opacity-50"
          >
            {generatingBrief ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
            Generate Brief
          </button>
        </div>
        {briefResult?.success && briefResult.brief && (
          <div className="p-3 bg-purple-50 rounded text-sm text-gray-700 whitespace-pre-wrap">
            {typeof briefResult.brief === 'string' ? briefResult.brief : JSON.stringify(briefResult.brief, null, 2)}
          </div>
        )}
        {briefResult && !briefResult.success && (
          <div className="p-3 bg-red-50 rounded text-sm text-red-600 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {briefResult.error || 'Failed to generate brief'}
          </div>
        )}
        {!briefResult && (
          <p className="text-xs text-gray-400">Generate an AI brief from all prior interactions before your next meeting.</p>
        )}
      </div>

      {/* Quick Record Meeting */}
      {showRecordingForm === '__standalone__' ? (
        <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-purple-600" />
              <h4 className="text-sm font-semibold text-gray-900">Record or Upload Meeting</h4>
            </div>
            <button
              onClick={() => { setShowRecordingForm(null); setRecordingFile(null); setAudioUrl(''); }}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>

          {/* Meeting Details */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Meeting Title</label>
              <input
                type="text"
                value={meetingTitle}
                onChange={(e) => setMeetingTitle(e.target.value)}
                placeholder="e.g. Discovery Call with Centauri"
                className="w-full px-3 py-1.5 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Date</label>
              <input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                className="w-full px-3 py-1.5 border rounded-md text-sm"
              />
            </div>
          </div>

          {/* Mode Selector */}
          <div className="flex gap-1 border-b border-purple-200 pb-2">
            <button
              onClick={() => setRecordingMode('upload')}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                recordingMode === 'upload' ? 'bg-purple-600 text-white' : 'text-purple-700 hover:bg-purple-100'
              }`}
            >
              <Upload className="w-3 h-3" /> Upload File
            </button>
            <button
              onClick={() => setRecordingMode('record')}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                recordingMode === 'record' ? 'bg-purple-600 text-white' : 'text-purple-700 hover:bg-purple-100'
              }`}
            >
              <Mic className="w-3 h-3" /> Record Now
            </button>
            <button
              onClick={() => setRecordingMode('url')}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                recordingMode === 'url' ? 'bg-purple-600 text-white' : 'text-purple-700 hover:bg-purple-100'
              }`}
            >
              <ExternalLink className="w-3 h-3" /> URL
            </button>
          </div>

          {/* File Upload Mode */}
          {recordingMode === 'upload' && (
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,video/*,.mp3,.wav,.m4a,.webm,.mp4,.mov,.ogg"
                onChange={(e) => {
                  if (e.target.files?.[0]) setRecordingFile(e.target.files[0]);
                }}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  recordingFile ? 'border-green-300 bg-green-50' : 'border-purple-300 hover:border-purple-400'
                }`}
              >
                {recordingFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-900">{recordingFile.name}</span>
                    <span className="text-xs text-gray-500">
                      ({(recordingFile.size / (1024 * 1024)).toFixed(1)} MB)
                    </span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-6 h-6 mx-auto mb-1 text-purple-400" />
                    <p className="text-xs text-gray-600">
                      <span className="font-medium text-purple-600">Click to select</span> audio or video file
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">MP3, WAV, M4A, WebM, MP4, MOV</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* In-Browser Recording Mode */}
          {recordingMode === 'record' && (
            <MeetingRecorder
              onRecordingComplete={(result) => {
                setRecordingFile(new File([result.blob], `recording_${Date.now()}.webm`, { type: result.mimeType }));
                setRecordingMode('upload');
              }}
              onCancel={() => setShowRecordingForm(null)}
            />
          )}

          {/* URL Mode */}
          {recordingMode === 'url' && (
            <div className="space-y-2">
              <input
                type="text"
                value={audioUrl}
                onChange={(e) => setAudioUrl(e.target.value)}
                placeholder="https://... (Zoom, Teams, or direct audio URL)"
                className="flex-1 w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          )}

          {/* Submit Button */}
          {recordingMode !== 'record' && (
            <button
              onClick={handleStandaloneSubmit}
              disabled={processing === '__standalone__' || (!recordingFile && !audioUrl)}
              className="w-full px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {processing === '__standalone__'
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing...</>
                : <><Brain className="w-3.5 h-3.5" /> Save Meeting & Transcribe</>}
            </button>
          )}
          <p className="text-xs text-gray-400">Creates a meeting record and processes the recording for AI transcription, summary, and action items.</p>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => setShowRecordingForm('__standalone__')}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg text-purple-600 hover:bg-purple-50 hover:border-purple-400 transition-colors"
          >
            <Video className="w-5 h-5" />
            <span className="text-sm font-medium">Record or Upload</span>
          </button>
          <button
            onClick={() => setShowLogMeetingForm(true)}
            className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">Log Meeting</span>
          </button>
        </div>
      )}

      {/* Log Meeting Form (no recording) */}
      {showLogMeetingForm && !showRecordingForm && (
        <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">Log a Past Meeting</h4>
            <button onClick={() => setShowLogMeetingForm(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Title *</label>
              <input
                type="text"
                value={logMeetingTitle}
                onChange={(e) => setLogMeetingTitle(e.target.value)}
                placeholder="e.g. Demo Call"
                className="w-full px-3 py-1.5 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Date</label>
              <input
                type="date"
                value={logMeetingDate}
                onChange={(e) => setLogMeetingDate(e.target.value)}
                className="w-full px-3 py-1.5 border rounded-md text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Notes</label>
            <textarea
              value={logMeetingNotes}
              onChange={(e) => setLogMeetingNotes(e.target.value)}
              rows={2}
              placeholder="Meeting notes, key discussion points..."
              className="w-full px-3 py-1.5 border rounded-md text-sm"
            />
          </div>
          <button
            onClick={handleLogMeeting}
            disabled={loggingMeeting || !logMeetingTitle.trim()}
            className="w-full px-3 py-2 text-sm bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {loggingMeeting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
            Log Meeting
          </button>
          <p className="text-xs text-gray-400">You can upload a recording to this meeting later from the Past Meetings section.</p>
        </div>
      )}

      {/* Section Toggle */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveSection('upcoming')}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeSection === 'upcoming' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'
          }`}
        >
          <Calendar className="w-3.5 h-3.5 inline mr-1" />
          Upcoming ({upcomingMeetings.length})
        </button>
        <button
          onClick={() => setActiveSection('past')}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeSection === 'past' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'
          }`}
        >
          <Clock className="w-3.5 h-3.5 inline mr-1" />
          Past Meetings ({activities.filter(a => a.completed).length})
        </button>
      </div>

      {/* Upcoming Meetings */}
      {activeSection === 'upcoming' && (
        <div className="space-y-3">
          {upcomingMeetings.length === 0 && activities.filter(a => !a.completed).length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No upcoming meetings</p>
            </div>
          ) : (
            <>
              {upcomingMeetings.map((m) => (
                <div key={m.activityId} className="border rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{m.title}</span>
                      {m.startTime && (
                        <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {new Date(m.startTime).toLocaleString()}
                        </div>
                      )}
                      {m.agenda && <p className="text-xs text-gray-500 mt-1">{m.agenda}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      {m.meetingLink && (
                        <a href={m.meetingLink} target="_blank" rel="noopener noreferrer"
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1">
                          <Play className="w-3 h-3" /> Join
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {activities.filter(a => !a.completed).map((a) => (
                <div key={a.id} className="border rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{a.title}</span>
                      {a.dueDate && (
                        <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {new Date(a.dueDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Past Meetings */}
      {activeSection === 'past' && (
        <div className="space-y-3">
          {activities.filter(a => a.completed).length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <Mic className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No completed meetings</p>
            </div>
          ) : (
            activities.filter(a => a.completed).map((activity) => (
              <div key={activity.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-gray-900">{activity.title}</span>
                    </div>
                    {activity.completedAt && (
                      <span className="text-xs text-gray-400 ml-6">
                        Completed {new Date(activity.completedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Upload Recording */}
                    <button
                      onClick={() => setShowRecordingForm(showRecordingForm === activity.id ? null : activity.id)}
                      className="p-1 text-gray-400 hover:text-purple-600"
                      title="Process recording"
                    >
                      <Mic className="w-4 h-4" />
                    </button>
                    {/* View Transcription */}
                    <button
                      onClick={() => handleViewTranscription(activity.id)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="View transcription"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    {/* Generate Follow-up */}
                    <button
                      onClick={() => handleGenerateFollowUp(activity.id)}
                      disabled={generatingFollowUp === activity.id}
                      className="p-1 text-gray-400 hover:text-green-600"
                      title="Generate follow-up"
                    >
                      {generatingFollowUp === activity.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Recording Upload / Record Form */}
                {showRecordingForm === activity.id && (
                  <div className="mt-2 p-3 bg-purple-50 rounded-lg space-y-3">
                    {/* Mode Selector */}
                    <div className="flex gap-1 border-b border-purple-200 pb-2">
                      <button
                        onClick={() => setRecordingMode('upload')}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                          recordingMode === 'upload' ? 'bg-purple-600 text-white' : 'text-purple-700 hover:bg-purple-100'
                        }`}
                      >
                        <Upload className="w-3 h-3" /> Upload File
                      </button>
                      <button
                        onClick={() => setRecordingMode('record')}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                          recordingMode === 'record' ? 'bg-purple-600 text-white' : 'text-purple-700 hover:bg-purple-100'
                        }`}
                      >
                        <Mic className="w-3 h-3" /> Record Now
                      </button>
                      <button
                        onClick={() => setRecordingMode('url')}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                          recordingMode === 'url' ? 'bg-purple-600 text-white' : 'text-purple-700 hover:bg-purple-100'
                        }`}
                      >
                        <ExternalLink className="w-3 h-3" /> URL
                      </button>
                    </div>

                    {/* File Upload Mode */}
                    {recordingMode === 'upload' && (
                      <div className="space-y-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="audio/*,video/*,.mp3,.wav,.m4a,.webm,.mp4,.mov,.ogg"
                          onChange={(e) => {
                            if (e.target.files?.[0]) setRecordingFile(e.target.files[0]);
                          }}
                          className="hidden"
                        />
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                            recordingFile ? 'border-green-300 bg-green-50' : 'border-purple-300 hover:border-purple-400'
                          }`}
                        >
                          {recordingFile ? (
                            <div className="flex items-center justify-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-gray-900">{recordingFile.name}</span>
                              <span className="text-xs text-gray-500">
                                ({(recordingFile.size / (1024 * 1024)).toFixed(1)} MB)
                              </span>
                            </div>
                          ) : (
                            <div>
                              <Upload className="w-6 h-6 mx-auto mb-1 text-purple-400" />
                              <p className="text-xs text-gray-600">
                                <span className="font-medium text-purple-600">Click to select</span> audio or video file
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">MP3, WAV, M4A, WebM, MP4, MOV</p>
                            </div>
                          )}
                        </div>
                        {recordingFile && (
                          <button
                            onClick={() => handleFileUpload(activity.id, recordingFile)}
                            disabled={processing === activity.id}
                            className="w-full px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-1"
                          >
                            {processing === activity.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Brain className="w-3.5 h-3.5" />}
                            Upload & Transcribe
                          </button>
                        )}
                      </div>
                    )}

                    {/* In-Browser Recording Mode */}
                    {recordingMode === 'record' && (
                      <MeetingRecorder
                        onRecordingComplete={(result) => handleRecordingComplete(activity.id, result)}
                        onCancel={() => setShowRecordingForm(null)}
                      />
                    )}

                    {/* URL Mode (legacy) */}
                    {recordingMode === 'url' && (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={audioUrl}
                            onChange={(e) => setAudioUrl(e.target.value)}
                            placeholder="https://... (Zoom, Teams, or direct audio URL)"
                            className="flex-1 px-3 py-2 border rounded-md text-sm"
                          />
                          <button
                            onClick={() => handleProcessRecording(activity.id)}
                            disabled={!audioUrl || processing === activity.id}
                            className="px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {processing === activity.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Brain className="w-3.5 h-3.5" />}
                            Process
                          </button>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-400">Transcribes audio via AssemblyAI, generates AI summary, extracts action items & creates tasks.</p>
                  </div>
                )}

                {/* Transcription Viewer */}
                {viewingTranscription === activity.id && (
                  <div className="mt-2 border-t pt-2">
                    {loadingTranscription ? (
                      <div className="flex items-center gap-1 text-xs text-gray-500 py-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Loading transcription...
                      </div>
                    ) : transcription ? (
                      <div className="space-y-3">
                        {/* Status Badge */}
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${
                            transcription.status === 'completed' ? 'bg-green-100 text-green-700' :
                            transcription.status === 'failed' ? 'bg-red-100 text-red-600' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {transcription.status}
                          </span>
                          {transcription.durationSeconds && (
                            <span className="text-gray-400">{Math.round(transcription.durationSeconds / 60)} min</span>
                          )}
                          {transcription.speakerCount && (
                            <span className="text-gray-400">{transcription.speakerCount} speakers</span>
                          )}
                        </div>

                        {/* AI Summary */}
                        {transcription.aiSummary && (
                          <div>
                            <h5 className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" /> AI Summary
                            </h5>
                            <p className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{transcription.aiSummary}</p>
                          </div>
                        )}

                        {/* Key Points */}
                        {transcription.aiKeyDiscussionPoints && transcription.aiKeyDiscussionPoints.length > 0 && (
                          <div>
                            <h5 className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                              <ListChecks className="w-3 h-3" /> Key Points
                            </h5>
                            <ul className="mt-1 space-y-0.5">
                              {transcription.aiKeyDiscussionPoints.map((point: any, i: number) => (
                                <li key={i} className="text-xs text-gray-700 flex items-start gap-1">
                                  <span className="text-blue-400 mt-0.5">&#8226;</span>
                                  {typeof point === 'string' ? point : point.point || JSON.stringify(point)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Sentiment */}
                        {transcription.aiSentimentAnalysis && (
                          <div className="flex items-center gap-2 text-xs">
                            <TrendingUp className="w-3 h-3 text-gray-400" />
                            <span className="text-gray-600">Sentiment:</span>
                            <span className={`font-medium ${
                              typeof transcription.aiSentimentAnalysis === 'string' && transcription.aiSentimentAnalysis.includes('positive') ? 'text-green-600' :
                              typeof transcription.aiSentimentAnalysis === 'string' && transcription.aiSentimentAnalysis.includes('negative') ? 'text-red-600' :
                              'text-gray-600'
                            }`}>
                              {typeof transcription.aiSentimentAnalysis === 'string'
                                ? transcription.aiSentimentAnalysis
                                : JSON.stringify(transcription.aiSentimentAnalysis)}
                            </span>
                          </div>
                        )}

                        {/* Extracted Needs */}
                        {transcription.aiExtractedNeeds && transcription.aiExtractedNeeds.length > 0 && (
                          <div>
                            <h5 className="text-xs font-semibold text-gray-600">Extracted Prospect Needs</h5>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {transcription.aiExtractedNeeds.map((need: any, i: number) => (
                                <span key={i} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full">
                                  {typeof need === 'string' ? need : need.need || JSON.stringify(need)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Error */}
                        {transcription.errorMessage && (
                          <div className="flex items-center gap-1 text-xs text-red-500">
                            <AlertCircle className="w-3 h-3" />
                            {transcription.errorMessage}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 py-2">No transcription available. Upload a recording to get started.</p>
                    )}
                  </div>
                )}

                {/* Follow-up Result */}
                {followUpResult?.activityId === activity.id && followUpResult.data?.success && (
                  <div className="mt-2 p-3 bg-green-50 rounded-lg space-y-2">
                    <h5 className="text-xs font-semibold text-green-700 flex items-center gap-1">
                      <Send className="w-3 h-3" /> AI Follow-Up Draft
                    </h5>
                    {followUpResult.data.followUpDraft && (
                      <div className="text-xs text-gray-700 whitespace-pre-wrap bg-white p-2 rounded border">
                        {followUpResult.data.followUpDraft}
                      </div>
                    )}
                    {followUpResult.data.taskList && followUpResult.data.taskList.length > 0 && (
                      <div>
                        <h6 className="text-xs font-medium text-green-600 mt-2">Suggested Tasks</h6>
                        <ul className="mt-1 space-y-0.5">
                          {followUpResult.data.taskList.map((task: any, i: number) => (
                            <li key={i} className="text-xs text-gray-700 flex items-start gap-1">
                              <ListChecks className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                              {typeof task === 'string' ? task : task.title || JSON.stringify(task)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
